import { useEffect, useRef, useState } from 'react'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  BarChart3,
  Boxes,
  ChevronsLeft,
  ChevronsRight,
  FolderTree,
  History,
  Layers,
  LayoutDashboard,
  LogOut,
  Mail,
  Menu,
  Package,
  Settings,
  ShoppingCart,
  Truck,
  Users,
  UsersRound,
  X,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { LOGIN_PATH } from '@/auth/redirect'
import { useAuth } from '@/auth/useAuth'
import type { Permission } from '@/auth/permissions'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { env } from '@/config/env'
import { cn } from '@/lib/cn'

type IconComponent = ComponentType<{ className?: string }>

type NavItem = {
  label: string
  to: string
  icon: IconComponent
  /** Only the dashboard needs exact matching; the rest own their subtrees. */
  end?: boolean
  /**
   * The permission that makes this screen worth showing.
   *
   * Hidden when absent. This is presentation: the API refuses the same calls with 403 whatever
   * is rendered. What it buys is a sidebar that describes the job the person actually has,
   * instead of a list of doors that open onto a refusal.
   */
  permission: Permission
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard, end: true, permission: 'dashboard:read' },
  { label: 'Orders', to: '/orders', icon: ShoppingCart, permission: 'order:read' },
  { label: 'Shipments', to: '/shipments', icon: Truck, permission: 'shipment:read' },
  { label: 'Products', to: '/products', icon: Package, permission: 'catalogue:read' },
  { label: 'Inventory', to: '/inventory', icon: Boxes, permission: 'inventory:read' },
  { label: 'Categories', to: '/categories', icon: FolderTree, permission: 'catalogue:read' },
  { label: 'Collections', to: '/collections', icon: Layers, permission: 'catalogue:read' },
  { label: 'Customers', to: '/customers', icon: Users, permission: 'customer:read' },
  { label: 'Notifications', to: '/notifications', icon: Mail, permission: 'notification:read' },
  { label: 'Activity', to: '/activity', icon: History, permission: 'activity:read' },
  { label: 'Reports', to: '/reports', icon: BarChart3, permission: 'report:read' },
  { label: 'Staff', to: '/staff', icon: UsersRound, permission: 'staff:manage' },
  { label: 'Settings', to: '/settings', icon: Settings, permission: 'settings:manage' },
]

const SIDEBAR_STORAGE_KEY = 'store-admin.sidebar-collapsed'

export function AdminLayout() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const [collapsed, setCollapsed] = useState(() => readCollapsedPreference())
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)

  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const drawerRef = useRef<HTMLDivElement>(null)

  // A drawer left open across a navigation covers the page the user just chose.
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!drawerOpen) return

    drawerRef.current?.focus({ preventScroll: true })

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setDrawerOpen(false)
        menuButtonRef.current?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [drawerOpen])

  function toggleCollapsed() {
    setCollapsed((current) => {
      const next = !current
      try {
        window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      } catch {
        // Preference only — a storage failure must not break the layout.
      }
      return next
    })
  }

  async function handleSignOut() {
    setSigningOut(true)
    try {
      await logout()
      navigate(LOGIN_PATH, { replace: true })
    } finally {
      setSigningOut(false)
    }
  }

  return (
    // `data-print` marks what is app chrome rather than content — the print
    // rules in index.css hide it so a packing slip comes out on one clean sheet.
    <div data-print="shell" className="min-h-dvh bg-canvas">
      <a
        data-print="hide"
        href="#main-content"
        className="sr-only rounded-md bg-brand-600 px-3 py-2 font-medium text-white focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[70]"
      >
        Skip to content
      </a>

      {/* Desktop rail */}
      <aside
        data-print="hide"
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-line bg-surface lg:flex',
          collapsed ? 'w-15' : 'w-60',
        )}
      >
        <SidebarBrand collapsed={collapsed} />
        <SidebarNav collapsed={collapsed} />

        <div className="border-t border-line p-2">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(collapsed ? 'justify-center px-0' : 'justify-start')}
            leadingIcon={
              collapsed ? (
                <ChevronsRight className="size-4" aria-hidden="true" />
              ) : (
                <ChevronsLeft className="size-4" aria-hidden="true" />
              )
            }
          >
            <span className={cn(collapsed && 'sr-only')}>Collapse</span>
          </Button>
        </div>
      </aside>

      {/* Mobile drawer */}
      {drawerOpen ? (
        <div data-print="hide" className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-surface-inverse/50"
            onClick={() => setDrawerOpen(false)}
            role="presentation"
          />
          <div
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
            tabIndex={-1}
            className="relative flex h-full w-72 max-w-[85%] flex-col bg-surface shadow-overlay"
          >
            <div className="flex items-center justify-between border-b border-line px-3 py-2.5">
              <SidebarBrand collapsed={false} bare />
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="rounded-md p-1.5 text-ink-subtle hover:bg-surface-sunken hover:text-ink"
              >
                <X className="size-4" aria-hidden="true" />
                <span className="sr-only">Close navigation</span>
              </button>
            </div>
            <SidebarNav collapsed={false} />
          </div>
        </div>
      ) : null}

      <div
        data-print="shell"
        className={cn('flex min-h-dvh flex-col', collapsed ? 'lg:pl-15' : 'lg:pl-60')}
      >
        <header
          data-print="hide"
          className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-surface/95 px-3 backdrop-blur sm:px-5"
        >
          <button
            ref={menuButtonRef}
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-expanded={drawerOpen}
            className="rounded-md p-1.5 text-ink-muted hover:bg-surface-sunken hover:text-ink lg:hidden"
          >
            <Menu className="size-5" aria-hidden="true" />
            <span className="sr-only">Open navigation</span>
          </button>

          <span className="truncate font-semibold text-ink lg:hidden">{env.storeName}</span>

          <div className="ml-auto flex items-center gap-2 sm:gap-3">
            {user ? (
              <div className="flex items-center gap-2">
                <div className="hidden text-right leading-tight sm:block">
                  <p className="font-medium text-ink">{user.fullName}</p>
                  <p className="text-xs text-ink-subtle">{user.email}</p>
                </div>
                {/* The role's own name, not a label looked up from a fixed list: a staff member
                      may be on a role somebody created, which no such list can name. */}
                  <Badge tone="brand">{user.roleName}</Badge>
              </div>
            ) : null}

            <Button
              variant="secondary"
              size="sm"
              onClick={handleSignOut}
              isLoading={signingOut}
              loadingText="Signing out…"
              leadingIcon={<LogOut className="size-4" aria-hidden="true" />}
            >
              <span className="hidden sm:inline">Sign out</span>
              <span className="sr-only sm:hidden">Sign out</span>
            </Button>
          </div>
        </header>

        <main data-print="shell" id="main-content" className="flex-1 px-3 py-5 sm:px-5 lg:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  )
}

function SidebarBrand({ collapsed, bare = false }: { collapsed: boolean; bare?: boolean }) {
  return (
    <div
      className={cn(
        'flex h-14 shrink-0 items-center gap-2 px-3',
        !bare && 'border-b border-line',
        collapsed && 'justify-center px-0',
      )}
    >
      <span
        className="flex size-7 shrink-0 items-center justify-center rounded-md bg-brand-600 text-xs font-bold text-white"
        aria-hidden="true"
      >
        {/* Placeholder mark. Replaced by a real logo once branding is decided. */}
        {env.storeName.slice(0, 1).toUpperCase()}
      </span>
      <span className={cn('min-w-0 truncate', collapsed && 'sr-only')}>
        <span className="block truncate font-semibold text-ink">{env.storeName}</span>
        <span className="block text-xs text-ink-subtle">Admin</span>
      </span>
    </div>
  )
}

function SidebarNav({ collapsed }: { collapsed: boolean }) {
  const { can } = useAuth()

  // Hidden rather than disabled. A greyed-out item invites a click and teaches nothing; an item
  // that is not there describes the job this person actually has. The route is guarded too, so a
  // bookmarked URL still explains itself rather than rendering an empty screen.
  const visible = NAV_ITEMS.filter((item) => can(item.permission))

  return (
    <nav aria-label="Main" className="flex-1 overflow-y-auto p-2">
      <ul className="space-y-0.5">
        {visible.map(({ label, to, icon: Icon, end }) => (
          <li key={to}>
            <NavLink
              to={to}
              end={end}
              title={collapsed ? label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2.5 rounded-md px-2.5 py-2 font-medium transition-colors',
                  collapsed && 'justify-center px-0',
                  isActive
                    ? 'bg-brand-50 text-brand-700'
                    : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
                )
              }
            >
              <Icon className="size-4 shrink-0" />
              <span className={cn(collapsed && 'sr-only')}>{label}</span>
            </NavLink>
          </li>
        ))}
      </ul>

    </nav>
  )
}

function readCollapsedPreference(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}
